/**
 * ChannelLinkResume — resumes a channel-link flow that detoured through SSO.
 *
 * The `/link/channel` landing page finishes the whole flow itself (sign in →
 * connect the provider → done), but an SSO-required workspace has to leave the
 * page: the workspace's SSO callback always lands on `/app`. So the page
 * stashes its own URL in sessionStorage before the detour, and this component —
 * mounted once in AppLayout — sends the user straight back to it on arrival.
 */

import { useEffect } from "react"
import { useNavigate } from "react-router-dom"

import { PENDING_CHANNEL_LINK_KEY } from "@/pages/link-channel"

export function ChannelLinkResume() {
  const navigate = useNavigate()

  useEffect(() => {
    let path: string | null = null
    try {
      const raw = sessionStorage.getItem(PENDING_CHANNEL_LINK_KEY)
      if (raw) {
        sessionStorage.removeItem(PENDING_CHANNEL_LINK_KEY)
        // Expiry guard: a failed SSO never reaches the app, so without it a
        // stale stash would hijack some later, unrelated sign-in.
        const parsed = JSON.parse(raw) as { path?: string; exp?: number }
        if (parsed?.path && typeof parsed.exp === "number" && parsed.exp > Date.now()) {
          path = parsed.path
        }
      }
    } catch {
      // Unavailable or malformed — nothing to resume.
    }
    // Only same-origin app paths, never an absolute URL.
    if (path && path.startsWith("/link/")) {
      navigate(path, { replace: true })
    }
  }, [navigate])

  return null
}

ChannelLinkResume.displayName = "ChannelLinkResume"
